import { PMS_BOARD_LABELS, pmsNightCount } from '@lemontaigu/pms-core';
import type {
  PlacementKind,
  ReshufflePlan,
  ReshuffleResult,
  ReshuffleRule,
  RuleBend,
  UpgradeProposal,
} from '@lemontaigu/pms-core';

// « Tetris » v2 in the desk's words — pure, so the wording is tested without a
// browser. The planning is pms-core's (`planRoomReshuffle`, `proposeUpgrades`);
// this turns its answers into what the inspector lists and the grid ghosts.
// docs/PMS-TETRIS-BRIEF.md §5–§6.

/** What the wording needs to name things. */
export interface PlanNames {
  /** The bar's name for a stay (occupant, else booker), and its dates. */
  stay: (id: string) => { name: string; checkinDate: string; checkoutDate: string } | null;
  room: (id: number) => string;
  /** The category of a room, e.g. « DSUP ». */
  categoryOfRoom: (id: number) => string | null;
}

/** One line of a plan. `stayId` names who a « ne pas déplacer » would protect. */
export interface RackPlanStep {
  text: string;
  stayId: string | null;
  warn: boolean;
}

/** What the grid draws for a plan: where each stay would land. */
export interface RackGhost {
  roomId: number;
  checkinDate: string;
  checkoutDate: string;
  label: string;
  kind: 'target' | 'moved';
}

export interface RackPlanView {
  kind: ReshuffleResult['kind'];
  headline: string;
  steps: RackPlanStep[];
  /** One line per rule bent, « Lit demandé non respecté — VASSEUR ». */
  bends: string[];
  /** Which plan this is, of how many (at most three). */
  index: number;
  count: number;
  ghosts: RackGhost[];
  /** Stays whose bar the plan takes out of its room — drawn as leaving. */
  moving: string[];
}

/** « 07/10 » — day and month, enough inside a week's plan. */
export const fmtDm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const span = (s: { checkinDate: string; checkoutDate: string }) => `${fmtDm(s.checkinDate)} → ${fmtDm(s.checkoutDate)}`;

const RULE_FR: Record<ReshuffleRule, string> = {
  bed: 'Lit demandé non respecté',
  freeze: 'Arrivée du jour ou réservation figée, déplacée',
  maxMoves: 'Plus de 3 déplacements',
  belowSold: 'Sous la catégorie vendue',
};

/** « — surclassement », « — retour à sa catégorie »… what a placement means. */
function kindFr(kind: PlacementKind, category: string | null): string {
  switch (kind) {
    case 'upgrade':
      return ` — surclassement${category ? ` (${category})` : ''}`;
    case 'downgrade':
      return ' — retour à sa catégorie';
    case 'below':
      return ` — déclassement${category ? ` (${category})` : ''}`;
    default:
      return '';
  }
}

const bedFr = (ready: boolean | null) => (ready === false ? ' — lit à refaire' : '');

/** The rules a plan bends, one line each, grouped by rule. */
export function bendsFr(bends: readonly RuleBend[], names: PlanNames): string[] {
  const by = new Map<ReshuffleRule, string[]>();
  for (const b of bends) {
    const who = b.rule === 'maxMoves' ? null : (names.stay(b.stayId)?.name ?? '?');
    const list = by.get(b.rule) ?? [];
    if (who !== null && !list.includes(who)) list.push(who);
    by.set(b.rule, list);
  }
  return [...by.entries()].map(([rule, who]) => (who.length > 0 ? `${RULE_FR[rule]} — ${who.join(', ')}` : RULE_FR[rule]));
}

function planSteps(plan: ReshufflePlan, targetId: string, names: PlanNames): RackPlanStep[] {
  const steps: RackPlanStep[] = [];
  const released = new Set(plan.released);
  for (const id of plan.released) {
    const s = names.stay(id);
    const from = plan.moves.find((m) => m.stayId === id)?.fromRoomId;
    steps.push({
      text: `Désaffecter ${s?.name ?? '?'} (${s ? span(s) : '?'})${from === undefined ? '' : ` de ${names.room(from)}`}`,
      stayId: id,
      warn: false,
    });
  }
  for (const m of plan.moves) {
    const s = names.stay(m.stayId);
    const who = `${s?.name ?? '?'} (${s ? span(s) : '?'})`;
    const what = released.has(m.stayId)
      ? `Réaffecter ${who} en ${names.room(m.toRoomId)}`
      : `Déplacer ${who} de ${names.room(m.fromRoomId)} vers ${names.room(m.toRoomId)}`;
    steps.push({
      text: `${what}${kindFr(m.kind, names.categoryOfRoom(m.toRoomId))}${bedFr(m.bedReady)}`,
      stayId: m.stayId,
      warn: m.kind === 'below' || m.bedReady === false,
    });
  }
  const t = names.stay(targetId);
  steps.push({
    text: `Placer ${t?.name ?? '?'} (${t ? span(t) : '?'}) en ${names.room(plan.targetRoomId)}${kindFr(
      plan.targetKind,
      names.categoryOfRoom(plan.targetRoomId),
    )}${bedFr(plan.targetBedReady)}`,
    stayId: null,
    warn: plan.targetKind === 'below' || plan.targetBedReady === false,
  });
  return steps;
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** The display model for one answer of `planRoomReshuffle`; `index` picks
 *  which of its plans (best first) when it has several. */
export function rackPlanView(result: ReshuffleResult, targetId: string, index: number, names: PlanNames): RackPlanView {
  const target = names.stay(targetId);
  const label = `${target?.name ?? '?'} ?`;
  switch (result.kind) {
    case 'plans': {
      const i = Math.min(Math.max(index, 0), result.plans.length - 1);
      const plan = result.plans[i]!;
      const bedChanges = plan.cost[3] ?? 0;
      const headline =
        plan.moves.length === 0
          ? 'Sans déplacement'
          : `${plural(plan.moves.length, 'déplacement', 'déplacements')}${
              bedChanges > 0 ? ` · ${plural(bedChanges, 'lit à refaire', 'lits à refaire')}` : ''
            }`;
      return {
        kind: 'plans',
        headline,
        steps: planSteps(plan, targetId, names),
        bends: bendsFr(plan.bends, names),
        index: i,
        count: result.plans.length,
        ghosts: [
          ...(target === null
            ? []
            : [{ roomId: plan.targetRoomId, checkinDate: target.checkinDate, checkoutDate: target.checkoutDate, label, kind: 'target' as const }]),
          ...plan.moves.flatMap((m) => {
            const s = names.stay(m.stayId);
            return s === null
              ? []
              : [{ roomId: m.toRoomId, checkinDate: s.checkinDate, checkoutDate: s.checkoutDate, label: `${s.name} →`, kind: 'moved' as const }];
          }),
        ],
        moving: plan.moves.map((m) => m.stayId),
      };
    }
    case 'split': {
      const steps = result.segments.map((g, i) => ({
        text: `${i === 0 ? 'Placer' : 'Puis'} ${target?.name ?? '?'} en ${names.room(g.roomId)} du ${fmtDm(g.from)} au ${fmtDm(g.to)}`,
        stayId: null,
        warn: false,
      }));
      return {
        kind: 'split',
        headline: `Changement de chambre en cours de séjour (${plural(result.segments.length - 1, 'changement', 'changements')})`,
        steps,
        bends: bendsFr(result.bends, names),
        index: 0,
        count: 1,
        ghosts: result.segments.map((g) => ({ roomId: g.roomId, checkinDate: g.from, checkoutDate: g.to, label, kind: 'target' as const })),
        moving: [],
      };
    }
    case 'overbooked':
      return {
        kind: 'overbooked',
        headline: 'Complet — même en levant toutes les règles',
        steps: result.shortfall.map((s) => ({
          text: `Il manque ${plural(s.missing, 'chambre', 'chambres')} la nuit du ${fmtDm(s.date)}`,
          stayId: null,
          warn: true,
        })),
        bends: [],
        index: 0,
        count: 1,
        ghosts: [],
        moving: [],
      };
    case 'unresolved':
      return {
        kind: 'unresolved',
        headline: 'Pas de solution en 5 déplacements — à voir à la main',
        steps: [],
        bends: [],
        index: 0,
        count: 1,
        ghosts: [],
        moving: [],
      };
  }
}


/** One line of the day-before review. */
export function upgradeLineFr(u: UpgradeProposal, names: PlanNames): string {
  const s = names.stay(u.stayId);
  const nights = s ? pmsNightCount(s.checkinDate, s.checkoutDate) : u.nights;
  const from =
    u.fromRoomId === null
      ? 'à attribuer'
      : `de ${names.room(u.fromRoomId)}${names.categoryOfRoom(u.fromRoomId) ? ` (${names.categoryOfRoom(u.fromRoomId)})` : ''}`;
  const to = `${names.room(u.toRoomId)}${names.categoryOfRoom(u.toRoomId) ? ` (${names.categoryOfRoom(u.toRoomId)})` : ''}`;
  const board = u.board === null ? '' : `, ${PMS_BOARD_LABELS[u.board] ?? u.board}`;
  return `Surclasser ${s?.name ?? '?'} (${plural(nights, 'nuit', 'nuits')}${board}) ${from} en ${to}`;
}
